/* eslint-disable no-nested-ternary */
import { FormHelperText, Grid, IconButton, Modal, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import React, { useEffect, useState } from "react";
import Loader from "utils/Loader";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import MDButton from "components/MDButton";
import { enqueueSnackbar } from "notistack";
import { getAPICall, postAPICall, putAPICall, deleteAPICall } from "lib/callApi";
import {
  GET_REPORTS_TO_USERS,
  FETCH_FUTURE_OUT_OF_OFFICE_DAYS,
  CREATE_OUT_OF_OFFICE_DAYS,
  CREATE_OUT_OF_OFFICE_DAYS_FOR_REPORTS_TO_USERS,
  EDIT_OUT_OF_OFFICE_DAYS_FOR_REPORTS_TO_USERS,
  DELETE_OUT_OF_OFFICE_DAYS,
  CREATE_COMPANY_HOLIDAYS,
  EDIT_COMPANY_HOLIDAYS,
} from "lib/constants";
import MultiSelectAutocomplete from "components/MultiSelectAutocomplete";
import Swal from "sweetalert2";
import theme from "assets/theme";
import { isEqual, pick } from "lodash";
import MDInput from "components/MDInput";

function SetOutOfOfficeDaysModal(props) {
  const {
    open,
    handleClose,
    title = "Set Out of Office",
    description,
    view,
    values = {},
    hideFutureDateSection = false,
    isEditing = false,
  } = props;

  const isTeamView = view === "team";
  const isCompanyView = view === "company";
  const user = useSelector((state) => state.loginUser.loginUserState);
  const defaultReportsToUsersList = [
    {
      id: user?.userCompanyLinkId,
      label: user?.name,
    },
  ];
  const [controller] = useMaterialUIController();
  const [isLoading, setIsLoading] = useState(false);
  const [isFutureOutOfDaysLoading, setIsFutureOutOfDaysLoading] = useState(true);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [error, setError] = useState("");
  const [reportsToUsersList, setReportsToUsersList] = useState(defaultReportsToUsersList);
  const [selectedReportsTo, setSelectedReportsTo] = useState(defaultReportsToUsersList);
  const [futureDates, setFutureDates] = useState([]);
  const [companyHolidayName, setCompanyHolidayName] = useState();
  const [shouldRefetchData, setShouldRefetchData] = useState(false);

  const fetchFutureOutOfDays = async (users) => {
    setIsFutureOutOfDaysLoading(true);
    if (hideFutureDateSection) return;
    try {
      if (!users?.length) {
        setFutureDates([]);
        return;
      }
      const futureOutOfDays = await getAPICall(FETCH_FUTURE_OUT_OF_OFFICE_DAYS, true, {
        users: users.join(),
      });
      if (futureOutOfDays?.data?.success) {
        const data = futureOutOfDays?.data?.data;
        const grouped = [];

        data.forEach((item) => {
          const lastGroup = grouped[grouped.length - 1];

          if (lastGroup && lastGroup.userCompanyLinkId === item.FK_user_companies_links_id) {
            const lastDay = new Date(lastGroup.days[lastGroup.days.length - 1].day);
            const currentDay = new Date(item.day);
            const diffDays = (currentDay - lastDay) / (1000 * 60 * 60 * 24);

            if (diffDays === 1) {
              lastGroup.days.push({
                day: item.day,
                year: item.year,
              });
            } else {
              grouped.push({
                userCompanyLinkId: item.FK_user_companies_links_id,
                name: item?.UserCompaniesLink?.name,
                days: [
                  {
                    day: item.day,
                    year: item.year,
                  },
                ],
              });
            }
          } else {
            grouped.push({
              userCompanyLinkId: item.FK_user_companies_links_id,
              name: item?.UserCompaniesLink?.name,
              days: [
                {
                  day: item.day,
                  year: item.year,
                },
              ],
            });
          }
        });

        setFutureDates(grouped || []);
      } else {
        setFutureDates([]);
        if (futureOutOfDays.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: futureOutOfDays.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsFutureOutOfDaysLoading(false);
    }
  };

  const fetchReportsToUsers = async () => {
    try {
      const roles = await getAPICall(`${GET_REPORTS_TO_USERS}/${user.id}`, true);
      if (roles?.data?.success) {
        const userCompanyLinkIds = [user.userCompanyLinkId];
        const newReportsToUsersList = [...defaultReportsToUsersList];

        roles?.data?.data?.forEach((reportToUser) => {
          userCompanyLinkIds.push(reportToUser?.id);
          newReportsToUsersList.push({
            id: reportToUser?.id,
            label: reportToUser?.name,
          });
        });

        setReportsToUsersList(newReportsToUsersList || []);
        fetchFutureOutOfDays(userCompanyLinkIds || []);
      } else {
        setReportsToUsersList(defaultReportsToUsersList);
        if (roles.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: roles.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (isTeamView) fetchReportsToUsers();
    else fetchFutureOutOfDays([user.userCompanyLinkId]);
  }, []);

  useEffect(() => {
    setStartDate(values?.startDate);
    setEndDate(values?.endDate);
    if (values?.companyHolidayName) {
      setCompanyHolidayName(values.companyHolidayName);
    }
  }, []);

  const { darkMode } = controller;

  const handleModalClose = (refetchCompanyHolidays = false, payload) => {
    if (!isLoading) {
      setIsLoading(false);
      handleClose(refetchCompanyHolidays || shouldRefetchData, payload);
    }
  };

  const editOutOfOfficeDays = async () => {
    try {
      setIsLoading(true);
      const payload = {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        ...(isTeamView
          ? { users: selectedReportsTo?.map((selectedUser) => selectedUser?.id) }
          : {}),
        oldStartDate: moment(values?.startDate).format("YYYY-MM-DD"),
        oldEndDate: moment(values?.endDate).format("YYYY-MM-DD"),
      };

      const result = await putAPICall(EDIT_OUT_OF_OFFICE_DAYS_FOR_REPORTS_TO_USERS, true, payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        handleModalClose(true, {
          startDate,
          endDate,
        });
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const createOutOfOfficeDays = async () => {
    try {
      setIsLoading(true);
      const payload = {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        ...(isTeamView
          ? { users: selectedReportsTo?.map((selectedUser) => selectedUser?.id) }
          : {}),
      };

      const result = await postAPICall(
        isTeamView ? CREATE_OUT_OF_OFFICE_DAYS_FOR_REPORTS_TO_USERS : CREATE_OUT_OF_OFFICE_DAYS,
        true,
        payload
      );

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        handleModalClose(true, {
          startDate,
          endDate,
        });
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const editCompanyHoliDays = async () => {
    try {
      setIsLoading(true);
      const payload = {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        companyHolidayName,
        oldStartDate: moment(values?.startDate).format("YYYY-MM-DD"),
        oldEndDate: moment(values?.endDate).format("YYYY-MM-DD"),
        key: values?.key,
      };

      const result = await putAPICall(EDIT_COMPANY_HOLIDAYS, true, payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        handleModalClose(true);
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const createNewCompanyHoliDays = async () => {
    try {
      setIsLoading(true);
      const payload = {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        companyHolidayName,
      };

      const result = await postAPICall(CREATE_COMPANY_HOLIDAYS, true, payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        handleModalClose(true);
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOutOfOfficeDays = async (renderedText, deletePayload) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${renderedText}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, delete it!",
      customClass: {
        container: "swal-modal-index",
      },
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const params = new URLSearchParams();

          params.set("key", deletePayload?.name);
          params.set("userCompanyLinkId", deletePayload?.userCompanyLinkId);
          deletePayload?.days?.forEach((item) => params.append("days", item?.day));

          const resultResponse = await deleteAPICall(DELETE_OUT_OF_OFFICE_DAYS, true, params);
          try {
            if (resultResponse?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: "Out Of Office Days successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );

              const newFutureDates = futureDates.filter(
                (futureDate) =>
                  !isEqual(pick(futureDate, Object.keys(deletePayload)), deletePayload)
              );
              setFutureDates(newFutureDates);
              setShouldRefetchData(true);
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: resultResponse?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
            }
          } catch (err) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: err.response.data.message || err.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: err.response.data.message,
          }),
          {
            variant: "custom",
          }
        );
      });
  };

  const handleSubmit = () => {
    setError("");
    let errorMessage = "";
    if (!startDate && !endDate) {
      errorMessage = "Start date and End date is required";
    } else if (!startDate) {
      errorMessage = "Start date is required";
    } else if (!endDate) {
      errorMessage = "End date is required";
    } else if (moment(endDate).isBefore(startDate)) {
      errorMessage = "Selected date interval was not valid";
    } else if (isCompanyView && !companyHolidayName) {
      errorMessage = "Company holiday is required";
    }

    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    if (isEditing && isCompanyView) {
      editCompanyHoliDays();
    } else if (isEditing) {
      editOutOfOfficeDays();
    } else if (isCompanyView) createNewCompanyHoliDays();
    else createOutOfOfficeDays();
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "80%",
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: "0.5rem",
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const FutureDate = ({ futureDate }) => {
    if (!futureDate) return null;

    const { userCompanyLinkId, name, days } = futureDate;
    const renderedText = `${
      isTeamView ? `${userCompanyLinkId === user?.userCompanyLinkId ? "Me" : name || ""} - ` : ""
    }${moment(days[0].day).format("dddd, MMMM Do")}${
      days.length > 1 ? ` - ${moment(days[days.length - 1].day).format("dddd, MMMM Do")}` : ""
    }`;

    return (
      <Typography variant="subtitle2" key={renderedText}>
        <IconButton onClick={() => deleteOutOfOfficeDays(renderedText, futureDate)}>
          <CloseIcon />
        </IconButton>
        {renderedText}
      </Typography>
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => handleModalClose(shouldRefetchData)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox
        sx={{
          ...style,
          width: {
            xs: "90%",
            md: "600px",
          },
        }}
      >
        <Loader loading={isLoading || isFutureOutOfDaysLoading} />
        <MDBox mb={6}>
          <Typography variant="h4" mb={2}>
            {title}
          </Typography>
          <Typography variant="body1" mb={2}>
            {description || ""}
          </Typography>
        </MDBox>
        {isCompanyView ? (
          <MDBox mb={5}>
            <MDInput
              fullWidth
              label="Company Holiday"
              inputProps={{ autoComplete: "" }}
              name="companyHolidayName"
              error={companyHolidayName && companyHolidayName?.length === 0}
              helperText={companyHolidayName?.length === 0 && "Please enter company name"}
              value={companyHolidayName || ""}
              onChange={(e) => setCompanyHolidayName(e.target.value)}
            />
          </MDBox>
        ) : hideFutureDateSection ? null : (
          <MDBox mb={5}>
            <Typography variant="h4" mb={2}>
              Future Dates
            </Typography>
            <MDBox sx={{ maxHeight: "25vh", overflowY: "scroll" }}>
              {futureDates?.map((futureDate, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <FutureDate futureDate={futureDate} key={`${futureDate.name}-${index}`} />
              ))}
            </MDBox>
          </MDBox>
        )}

        <MDBox mb={10}>
          {isTeamView && reportsToUsersList.length > 1 ? (
            <MDBox mb={2}>
              <MultiSelectAutocomplete
                value={selectedReportsTo}
                options={reportsToUsersList}
                onChange={(newValue) => setSelectedReportsTo([...newValue])}
                label="Select Users"
                additionalTextFieldProps={{
                  variant: "standard",
                }}
              />
            </MDBox>
          ) : null}

          <Grid container spacing={3} p={1}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid item xs={12} sm={isCompanyView ? 6 : 5}>
                <DatePicker
                  label="Start"
                  placeholder="Start"
                  value={startDate || null}
                  format={user.dateFormat}
                  onChange={(value) => {
                    setError("");
                    setStartDate(value || null);
                  }}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      fullWidth: true,
                    },
                  }}
                  viewRenderers={{
                    hours: null,
                    minutes: null,
                    seconds: null,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={isCompanyView ? 6 : 5}>
                <DatePicker
                  label="End"
                  placeholder="End"
                  value={endDate || null}
                  format={user.dateFormat}
                  onChange={(value) => {
                    setError("");
                    setEndDate(value || null);
                  }}
                  viewRenderers={{
                    hours: null,
                    minutes: null,
                    seconds: null,
                  }}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>
            {!isCompanyView ? (
              <Grid item xs={12} sm={2} alignSelf="end">
                <MDButton disabled={isLoading} color="primaryRed" fullWidth onClick={handleSubmit}>
                  {isEditing ? "Edit" : "Add"}
                </MDButton>
              </Grid>
            ) : null}
          </Grid>
          {error ? <FormHelperText>{error}</FormHelperText> : null}
        </MDBox>
        {isCompanyView ? (
          <MDButton disabled={isLoading} color="primaryRed" fullWidth onClick={handleSubmit}>
            Save
          </MDButton>
        ) : null}
      </MDBox>
    </Modal>
  );
}

export default SetOutOfOfficeDaysModal;
